import { useMemo, useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { format, addDays } from "date-fns";
import { useBranch } from "@/context/BranchContext";
import ProfileMenu from "@/components/ProfileMenu";
import BranchGate from "@/components/BranchGate";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, CalendarClock, Table2, Calculator, ChevronLeft, ChevronRight, CalendarIcon } from "lucide-react";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/8d7b8938-50e0-4623-995d-c646de2c071c/images/e02cbf0ef82ec870ca0ecaca7a0a38d16a5e9cd0961708e76ecda7fbb2418529.png";

const TABS = [
  { to: "/schedule", label: "Schedule", icon: CalendarClock, testid: "tab-schedule" },
  { to: "/data", label: "Data Table", icon: Table2, testid: "tab-data" },
  { to: "/accounting", label: "Accounting", icon: Calculator, testid: "tab-accounting" },
];

export default function WorkspaceLayout() {
  const { selectedBranchId, currentBranch, branches, loading } = useBranch();
  const [selectedDate, setSelectedDate] = useState(new Date());

  const outletContext = useMemo(
    () => ({ selectedBranch: selectedBranchId, branches, selectedDate, setSelectedDate }),
    [selectedBranchId, branches, selectedDate]
  );

  const needsBranch = !selectedBranchId && branches.length > 1;
  const isToday = format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-cream">
      <header className="sticky top-0 z-30 glass-effect border-b border-border" data-testid="workspace-header">
        <div className="flex items-center justify-between px-4 lg:px-6 py-2.5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} className="w-9 h-9 object-contain" alt="Therapy Mana" />
            <div className="leading-tight">
              <h1 className="font-heading font-bold text-forest text-base">Therapy Mana</h1>
              <p className="text-[11px] text-sage" data-testid="workspace-branch-label">
                {currentBranch ? currentBranch.name : "Frontline"}
              </p>
            </div>
          </div>

          {/* Date selector */}
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setSelectedDate((d) => addDays(d, -1))} data-testid="date-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-8" data-testid="date-picker-btn">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {format(selectedDate, "EEE, d MMM")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} initialFocus />
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setSelectedDate((d) => addDays(d, 1))} data-testid="date-next"><ChevronRight className="h-4 w-4" /></Button>
            {!isToday && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setSelectedDate(new Date())} data-testid="date-today">Today</Button>}
          </div>

          <ProfileMenu />
        </div>

        {/* 3-Tab navigation */}
        <nav className="flex items-center gap-1 px-4 lg:px-6" data-testid="frontline-tabs">
          {TABS.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? "border-forest text-forest"
                    : "border-transparent text-sage hover:text-forest hover:border-border"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 overflow-auto p-4 lg:p-6 w-full" data-testid="workspace-content">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
          </div>
        ) : needsBranch ? (
          <BranchGate />
        ) : (
          <Outlet context={outletContext} />
        )}
      </main>
    </div>
  );
}
